// /src/pages/IndexPage.tsx
import { useBranding } from "../core/useBranding";
import { PublicShell } from "../ui/layout/PublicShell";
import { Badge } from "../ui/primitives/Badge";
import { ExternalLinkIcon } from "../ui/icons/ExternalLinkIcon";
import { stars } from "../lib/rating";

type FeaturedAgent = {
  title: string;
  description: string;    // short description of a benefit or purpose
  author: string;
  category: string;       // e.g., "Negotiation" | "Sales" | "Business development"
  reason?: string;        // e.g., "Most Popular" | "Highest rating" | "Recommended" | "New" | "Recent" | "Updated"
  rating?: number;        // e.g., 4.5
  href: string;
};

export function IndexPage() {
  const brand = useBranding();
  const brandName = brand.brandName;
  const heroImg = brand.heroImg!;

  // don't forget to enable those agents in DB, otherwese 404 will happen and user will see error page
  // update agents set state = 'enabled'
  // where agent_uuid in ('13cdf3e1-bddc-4488-94ad-02fa2fb4c29a', 'd638f798-15a5-4761-a9a2-7d962e91b4cf', '1c4fd932-e485-4bb4-88c0-8126d1b957b7')
  // then check:
  // select * from agents
  // where agent_uuid in ('13cdf3e1-bddc-4488-94ad-02fa2fb4c29a', 'd638f798-15a5-4761-a9a2-7d962e91b4cf', '1c4fd932-e485-4bb4-88c0-8126d1b957b7')
  const featuredAuthed: FeaturedAgent[] = [
    {
      title: "Use Time as a Strategic Tool",
      description: "Learn how to slow down, speed up, or apply deadlines deliberately so timing works in your favor in any negotiation.",
      author: "Herb Cohen",
      category: "Negotiation",
      reason: "New",
      //rating: 5.0,
      href: "/pages/portal/agent.html?agentId=13cdf3e1-bddc-4488-94ad-02fa2fb4c29a",
    },
    {
      title: "The Power of Assumptive Closing",
      description: "Practice guiding conversations toward a natural yes by confidently assuming the decision is made.",
      author: "Brian Tracy",
      category: "Sales",
      reason: "Recent",
      rating: 4.6,
      href: "/pages/portal/agent.html?agentId=d638f798-15a5-4761-a9a2-7d962e91b4cf",
    },
    {
      title: "Productizing Your Service",
      description: "Learn how to package your expertise into a clear, repeatable offer that makes buying an easy decision.",
      category: "Biz Strategy",
      author: "Ray J. Green",
      reason: "Updated",
      //rating: 4.5,
      href: "/pages/portal/agent.html?agentId=1c4fd932-e485-4bb4-88c0-8126d1b957b7",
    },
  ];

  const featuredAnon: FeaturedAgent[] = [
    {
      title: "Tactical Empathy Coach",
      description: "Practice recognizing and labeling emotions so the other side becomes more open to your influence.",
      category: "Negotiation",
      author: "Chris Voss",
      reason: "Most Popular",
      rating: 4.7,
      href: "/pages/embed/agent.html?agentId=fe6df680-770a-48c6-8129-d52221cde914",
    },
    {
      title: "Ideal Customer Profile Builder",
      description: "Clarify exactly who to pursue so your prospecting focuses on high-fit buyers. Stop wasting time on the wrong leads.",
      category: "Marketing",
      author: "Jeb Blount",
      reason: "Highest Rating",
      rating: 4.8,
      href: "/pages/embed/agent.html?agentId=1983386f-b7bd-490a-95e6-01a4a96cc9fa",
    },
    {
      title: "Turn Objections into Buying Signals",
      description: "Learn to recognize objections as signs of interest and respond in ways that advance the sale instead of stalling it.",
      category: "Sales",
      author: "Brian Tracy",
      reason: "Recommended",
      rating: 4.5,
      href: "/pages/embed/agent.html?agentId=cbc55c13-b45b-4a5e-8d01-96a95634f75d",
    },
  ];

  return (
    <PublicShell>
      {({ authed }) => {
        const featured = authed ? featuredAuthed : featuredAnon;
        return (
          <main>
            {/* Hero image with overlay */}
            <section className="py-0">
              <div className="mx-auto max-w-6xl">
                <div className="relative overflow-hidden rounded-sm ring-0 ring-border">
                  <div className="relative w-full aspect-[64/33] min-h-[550px]">
                    <img
                      src={heroImg}
                      alt=""
                      className="h-full w-full object-cover"
                      loading="eager"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/45 to-black/85" />

                    <div className="absolute inset-0">
                      <div className="flex h-full items-end p-6 md:p-10">
                        {/* Your overlay text block stays the same */}
                        <div className="max-w-screen-sm">
                          <div className="flex flex-wrap gap-2">
                            <Badge variant="bright">Clarity</Badge>
                            <Badge variant="bright">Actions</Badge>
                            <Badge variant="bright">Results</Badge>
                          </div>

                          <h1 className="mt-4 text-3xl font-semibold tracking-tight text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)] md:text-5xl">
                            AI Experts For Small Business
                          </h1>

                          <p className="mt-3 text-md font-semibold text-teal-100 md:text-base">
                            Figure out what to do next when things aren’t clear and stakes are high.
                          </p>

                          <p className="mt-3 text-md text-white/85 md:text-base">
                            <b>{brandName}</b> gives you AI agents to help you think through real business decisions.
                            They work inside your context and turn uncertainty into clear next steps and supportive assets.
                          </p>

                          <div className="mt-6 flex flex-wrap gap-3">
                            {authed ? (
                              <>
                                <a
                                  href="/pages/portal/index.html"
                                  className="inline-flex items-center rounded-xl bg-white px-4 py-2 text-sm font-semibold text-black hover:bg-white/90"
                                >
                                  Open Your Portal
                                </a>
                              </>
                            ) : (
                              <>
                                <a
                                  href="/pages/profile/index.html"
                                  className="inline-flex items-center rounded-xl bg-white/10 px-4 py-2 text-sm font-semibold text-white ring-1 ring-white/25 hover:bg-white/15"
                                >
                                  Create Your Profile
                                </a>
                              </>
                            )}
                          </div>
                          {authed ? (
                            <>
                            </>
                          ) : (
                            <div className="mt-3 text-xs text-white/70">
                              Free plan is available to unlock the full portal.
                            </div>
                          )}

                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Featured agents (simpler, less text) */}
            <section className="mx-auto max-w-6xl px-4 py-5">
              <div className="flex items-end justify-between gap-4">
                {authed ? (
                  <>
                    <div>
                      <h2 className="text-lg font-semibold">Here's what's new:</h2>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <h2 className="text-lg font-semibold">Start with an agent designed for one of the vital business functions:</h2>
                      <p className="mt-1 text-sm text-muted">
                        Create a profile to see the full library.
                      </p>
                    </div>
                  </>
                )
                }
              </div>

              <div className="mt-5 grid gap-4 md:grid-cols-3">
                {featured.map((a) => (
                  <a
                    key={a.title}
                    href={a.href}
                    target="_blank"
                    className="block rounded-2xl border border-border bg-card px-5 py-3 shadow-soft transition-shadow hover:shadow-md"
                  >
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Badge variant="meta">{a.category}</Badge>
                      <span className="text-xs text-muted">·</span>
                      <Badge variant="soft">{a.reason}</Badge>
                      <span className="text-xs text-muted">·</span>
                      {a.rating == null ? (
                        <>
                          <span className="text-xs text-muted">Not rated yet</span>
                        </>
                      ) : (
                        <>
                          <span className="text-xs text-yellow-500" aria-label={`Rating ${a.rating} out of 5`}>
                            {stars(a.rating)}
                          </span>
                          <span className="text-xs text-muted"> {a.rating}</span>
                        </>
                      )}
                    </div>
                    <div className="mt-2 flex items-start justify-between gap-3">
                      <div className="font-semibold text-primary">{a.title}</div>
                    </div>
                    <div className="text-sm text-muted italic">By {a.author}</div>
                    <p className="mt-2 text-sm">{a.description}</p>
                    <div className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary">
                      Try it
                      <ExternalLinkIcon className="h-4 w-4 opacity-70" />
                    </div>
                  </a>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                {authed ? (
                  <a
                    href="/pages/portal/index.html"
                    className="text-sm font-semibold text-primary hover:underline underline-offset-4"
                  >
                    Go to your portal →
                  </a>
                ) : (
                  <a
                    href="/pages/profile/index.html"
                    className="text-sm font-semibold text-primary hover:underline underline-offset-4"
                  >
                    Create free profile →
                  </a>
                )}
              </div>
            </section>
          </main>
        );
      }}
    </PublicShell>
  );
}
